import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Typography
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Pricing", path: "/pricing" },
  { name: "Contact", path: "/contact" }
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1200 }}
    >
      <AppBar
        elevation={0}
        position="static"
        sx={{
          bgcolor: isScrolled ? "rgba(255,255,255,0.8)" : "transparent",
          backdropFilter: isScrolled ? "blur(24px)" : "none",
          borderBottom: isScrolled ? "1px solid rgba(255,255,255,0.2)" : "none",
          py: isScrolled ? "12px" : "20px",
          transition: "all 500ms ease",
          boxShadow: isScrolled ? "var(--shadow-soft)" : "none"
        }}
      >
        <Box className="container-custom" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {/* Logo */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              textDecoration: "none"
            }}
          >
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "transform 300ms ease"
                }}
              >
                <FavoriteRoundedIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  inset: -4,
                  borderRadius: "12px",
                  bgcolor: "rgba(39,165,153,0.2)",
                  filter: "blur(12px)",
                  opacity: 0,
                  transition: "opacity 300ms ease",
                  pointerEvents: "none"
                }}
                className="logo-glow"
              />
            </Box>

            <Typography sx={{ fontSize: 20, fontWeight: 700, color: "text.primary" }}>
              Rite
              <Box component="span" sx={{ color: "primary.main" }}>
                Wellness
              </Box>
            </Typography>
          </Box>

          {/* Desktop links */}
          <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", gap: 0.5 }}>
            {navLinks.map((link) => {
              const active = location.pathname === link.path;
              return (
                <Box
                  key={link.path}
                  component={Link}
                  to={link.path}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: "10px",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    textDecoration: "none",
                    color: active ? "primary.main" : "text.secondary",
                    bgcolor: active ? "rgba(39,165,153,0.1)" : "transparent",
                    transition: "all 300ms ease",
                    "&:hover": {
                      color: active ? "primary.main" : "text.primary",
                      bgcolor: active ? "rgba(39,165,153,0.1)" : "hsl(var(--muted))"
                    }
                  }}
                >
                  {link.name}
                </Box>
              );
            })}
          </Box>

          {/* Desktop auth */}
          <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", gap: 1.5 }}>
            <Button
              component={Link}
              to="/signin"
              variant="text"
              size="small"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                "&:hover": { color: "text.primary", bgcolor: "transparent" }
              }}
            >
              Sign In
            </Button>
            <Button
              component={Link}
              to="/signup"
              variant="contained"
              size="small"
              sx={{
                bgcolor: "primary.main",
                color: "#fff",
                boxShadow: "var(--shadow-glow)",
                "&:hover": { bgcolor: "rgba(39,165,153,0.9)" }
              }}
            >
              Get Started
            </Button>
          </Box>

          {/* Mobile */}
          <IconButton
            onClick={() => setIsOpen((v) => !v)}
            sx={{
              display: { xs: "inline-flex", lg: "none" },
              p: 1,
              borderRadius: "10px",
              "&:hover": { bgcolor: "hsl(var(--muted))" }
            }}
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <CloseRoundedIcon sx={{ color: "text.primary" }} />
            ) : (
              <MenuRoundedIcon sx={{ color: "text.primary" }} />
            )}
          </IconButton>
        </Box>

        {/* Mobile dropdown */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              style={{ overflow: "hidden" }}
            >
              <Box className="container-custom" sx={{ mt: 2, display: { lg: "none" } }}>
                <Box className="glass-card" sx={{ p: 2 }}>
                  <Box sx={{ display: "grid", gap: 1 }}>
                    {navLinks.map((link, index) => {
                      const active = location.pathname === link.path;
                      return (
                        <motion.div
                          key={link.path}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Box
                            component={Link}
                            to={link.path}
                            sx={{
                              display: "block",
                              px: 2,
                              py: 1.5,
                              borderRadius: "10px",
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              textDecoration: "none",
                              color: active ? "primary.main" : "text.secondary",
                              bgcolor: active ? "rgba(39,165,153,0.1)" : "transparent",
                              "&:hover": {
                                color: active ? "primary.main" : "text.primary",
                                bgcolor: active ? "rgba(39,165,153,0.1)" : "hsl(var(--muted))"
                              }
                            }}
                          >
                            {link.name}
                          </Box>
                        </motion.div>
                      );
                    })}
                  </Box>

                  <Box sx={{ pt: 2, mt: 2, borderTop: "1px solid hsl(var(--border))", display: "grid", gap: 1 }}>
                    <Button component={Link} to="/signin" variant="outlined" fullWidth>
                      Sign In
                    </Button>
                    <Button
                      component={Link}
                      to="/signup"
                      variant="contained"
                      fullWidth
                      sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "rgba(39,165,153,0.9)" } }}
                    >
                      Get Started
                    </Button>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </AppBar>
    </motion.header>
  );
}
